import { useState } from 'react';
import { Client } from '../types';
import { updateClient } from '../services/api';

interface UseClientEditReturn {
  editedClient: Client | null;
  errors: {
    name: string;
    surname: string;
    email: string;
    mobile_number: string;
    middle_names: string;
    nickname: string;
    date_of_birth: string;
    id_passport_number: string;
    telephone_number: string;
    fax_number: string;
    marital_status: string;
    gender: string;
    language: string;
    drivers_license_codes: string;
    employer_name: string;
    tax_number: string;
    job_description: string;
    consultant: string;
    personal: string;
    title: string;
    server: string;
  };
  handleSubmit: (e: React.FormEvent) => Promise<Client | null>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  reset: () => void;
  updateClientField: (field: string, value: any) => void;
}

export const useClientEdit = (initialClient: Client | null, clientId?: string) => {
  const [editedClient, setEditedClient] = useState<Client | null>(
    initialClient
      ? {
          ...initialClient,
          title: initialClient.title || '',
          consultant: (initialClient as any)?.consultant_id || initialClient.consultant || '',
        }
      : null
  );
  const [errors, setErrors] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    middle_names: '',
    nickname: '',
    date_of_birth: '',
    id_passport_number: '',
    telephone_number: '',
    fax_number: '',
    marital_status: '',
    gender: '',
    language: '',
    drivers_license_codes: '',
    employer_name: '',
    tax_number: '',
    job_description: '',
    consultant: '',
    personal: '',
    title: '',
    status: '',
    server: '',
  });

  // updateClientField function
  const updateClientField = (field: string, value: any) => {
    console.log(`🔄 updateClientField: ${field} =`, value);
    setEditedClient((prev) => {
      if (!prev) return prev;
      return { ...prev, [field]: value };
    });
  };

  const reset = () => {
    if (initialClient) {
      setEditedClient({
        ...initialClient,
        consultant: (initialClient as any)?.consultant_id || initialClient.consultant || '',
      });
    }
    setErrors({
      name: '',
      surname: '',
      email: '',
      phone: '',
      middle_names: '',
      nickname: '',
      date_of_birth: '',
      id_passport_number: '',
      telephone_number: '',
      fax_number: '',
      marital_status: '',
      gender: '',
      language: '',
      drivers_license_codes: '',
      employer_name: '',
      tax_number: '',
      job_description: '',
      consultant: '',
      personal: '',
      title: '',
      status: '',
      server: '',
    });
  };

  const validateForm = () => {
  let isValid = true;
  const newErrors = {
    name: '',
    surname: '',
    email: '',
    phone: '',
    middle_names: '',
    nickname: '',
    date_of_birth: '',
    id_passport_number: '',
    telephone_number: '',
    fax_number: '',
    marital_status: '',
    gender: '',
    language: '',
    drivers_license_codes: '',
    employer_name: '',
    tax_number: '',
    job_description: '',
    consultant: '',
    personal: '',
    title: '',
    status: '',
    server: '',
  };

  console.log('🔧 [VALIDATION] Starting validation');

  if (!editedClient) {
    console.log('🔧 [VALIDATION] Error: editedClient is null');
    newErrors.server = 'Client data is missing';
    isValid = false;
  } else {
    console.log('🔧 [VALIDATION] Checking required fields...');
    
    // Only validate truly required fields
    if (!editedClient.name?.trim()) {
      console.log('🔧 [VALIDATION] Name missing');
      newErrors.name = 'Name is required';
      isValid = false;
    }
    
    if (!editedClient.surname?.trim()) {
      console.log('🔧 [VALIDATION] Surname missing');
      newErrors.surname = 'Surname is required';
      isValid = false;
    }
    
    if (!editedClient.email?.trim()) {
      console.log('🔧 [VALIDATION] Email missing');
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedClient.email)) {
      console.log('🔧 [VALIDATION] Invalid email format');
      newErrors.email = 'Invalid email format';
      isValid = false;
    }

    // Make all other fields optional for now
    console.log('🔧 [VALIDATION] All required fields present');
  }

  console.log('🔧 [VALIDATION] Final result:', isValid);
  setErrors(newErrors);
  return isValid;
};

  // In useClientEdit.ts, update the handleSubmit function to ensure driver licenses are included:
const handleSubmit = async (e: React.FormEvent): Promise<Client | null> => {
  e.preventDefault();
  
  console.log('🔧 [HOOK] handleSubmit called');
  console.log('🔧 [HOOK] Current editedClient:', editedClient);
  
  if (!clientId || !editedClient || !validateForm()) return null;

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token');
    }
    
    // Debug: Check what driver license data we have
    console.log('🔧 [HOOK] Driver licenses in editedClient:', editedClient.drivers_license_codes);
    
    // Only include fields that should be updatable (remove read-only fields)
    const clientToSend: any = {
      // Required fields
      name: editedClient.name,
      surname: editedClient.surname,
      email: editedClient.email,
      title: editedClient.title || 'Mr',
      mobile_number: editedClient.mobile_number || editedClient.phone || '',
      middle_names: editedClient.middle_names,
      nickname: editedClient.nickname,
      date_of_birth: editedClient.date_of_birth,
      id_passport_number: editedClient.id_number || editedClient.id_passport_number,
      telephone_number: editedClient.telephone_number,
      fax_number: editedClient.fax_number,
      marital_status: editedClient.marital_status,
      gender: editedClient.gender,
      language: editedClient.language,
      drivers_license_codes: editedClient.drivers_license_codes,
      employer_name: editedClient.employer_name,
      tax_number: editedClient.tax_number,
      job_description: editedClient.job_description,
      consultant: editedClient.consultant,
      personal: editedClient.personal,
      status: editedClient.status,
    };

    delete clientToSend.id;
    delete clientToSend.client_number;
    delete clientToSend.created_date;
    delete clientToSend.updated_date;
    
    // Remove any undefined or null values to reduce field count
    Object.keys(clientToSend).forEach(key => {
      if (clientToSend[key] === undefined || clientToSend[key] === null) {
        delete clientToSend[key];
      }
    });
    
    console.log('🔧 [HOOK] Fields being sent:', Object.keys(clientToSend));
    console.log('🔧 [HOOK] Driver licenses being sent:', clientToSend.drivers_license_codes);
    console.log('🔧 [HOOK] Number of fields:', Object.keys(clientToSend).length);

    const updatedClient = await updateClient(clientId, clientToSend, token);

    console.log('🔧 [HOOK] Update successful:', updatedClient);

    const finalClient: Client = {
      ...updatedClient,
    };
    setEditedClient(finalClient);
    return finalClient;
  } catch (error: any) {
    console.error('🔧 [HOOK] Error updating client:', error);
    setErrors((prev) => ({
      ...prev,
      server: error.response?.data?.message || error.message || 'Failed to update client',
    }));
    return null;
  }
};

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditedClient((prev) => {
      if (!prev) return prev;
      return { ...prev, [name]: value };
    });
    setErrors((prev) => ({ ...prev, [name]: '', server: '' }));
  };

// Return it from the hook:
return {
  editedClient,
  errors,
  handleSubmit,
  handleInputChange,
  reset,
  updateClientField,
};

};